/**
 * Typed API errors preserving status + structured validation details.
 */

export interface ApiErrorBody {
  error?: string
  message?: string
  status?: number
  /** Optional discriminator (e.g. "module_completion"). */
  kind?: string
  /** Structured validation error list (present on module completion failures). */
  errors?: string[]
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody
  readonly kind: string | null
  readonly details: string[] | null

  constructor(status: number, body: ApiErrorBody) {
    const message = body.error || body.message || `HTTP ${status}`
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
    this.kind = typeof body.kind === "string" ? body.kind : null
    this.details = Array.isArray(body.errors) ? body.errors.filter((e): e is string => typeof e === "string") : null
  }
}

/**
 * Extract validation error strings from any thrown value.
 * Returns the structured list if available, else a single-element array with the message,
 * else null when nothing useful can be extracted.
 */
export function extractValidationErrors(error: unknown): string[] | null {
  if (error instanceof ApiError) {
    if (error.details && error.details.length > 0) return error.details
    return error.message ? [error.message] : null
  }
  if (error instanceof Error && error.message) return [error.message]
  if (typeof error === "string" && error.length > 0) return [error]
  return null
}
